import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, CheckCircle2, Loader2 } from 'lucide-react';
import { API_BASE, PHONE_DISPLAY } from '../constants';
import { trackEvent } from '../analytics';
import {
  ENQUIRY_SERVICES, SERVICE_BY_KEY, MESSAGE_MAX, composeMessage,
  setEnquiryDraft, resetEnquiryDraft, useEnquirySmsHref, ROUTE_SERVICE,
} from '../lib/enquiry';
import './QuoteForm.css';

const initial = { name: '', phone: '', email: '', suburb: '', timing: '', message: '' };

// Strict-enough email format check. We only run this if the user actually
// typed something in the (optional) email field. Empty stays empty and the
// backend simply omits the Reply-To — a bounced reply is worse than none.
const EMAIL_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~](?:[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]*[A-Za-z0-9!#$%&'*+/=?^_`{|}~])?@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;

// AU phone formats: 04XX XXX XXX, +614XXXXXXXX, landlines 0[2378] XXXX XXXX,
// +61[2378]... — spaces, hyphens and parentheses are ignored before testing.
const AU_PHONE_RE = /^(?:\+61[23478]\d{8}|0[23478]\d{8})$/;
const normalisePhone = (v) => v.replace(/[\s()-]/g, '');

// B02: service is prefilled from the route (correctable), suburb + preferred
// timing are optional, guidance follows the SELECTED service. Everything the
// visitor types is preserved on every error path; the composed message is
// length-checked against the backend contract BEFORE the single POST.
const QuoteForm = () => {
  const formRef = useRef(null);
  const submittingRef = useRef(false);
  const requestRef = useRef(null);
  const [extrasOpen, setExtrasOpen] = useState(false);
  // Initial (correctable) service for the current route.
  const routeService = ROUTE_SERVICE[useLocation().pathname] || '';
  const [service, setService] = useState(routeService);
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  // Inline per-field errors (name / phone) — the typed text is never cleared
  // on error, so nothing the visitor wrote is ever lost.
  const [fieldErrors, setFieldErrors] = useState({});
  // Pre-fill context stashed via the 'ff-prefill-quote' custom event from
  // <DesignView>. Sent to the backend so the notification email carries the
  // design image + category and Corey knows exactly which design triggered it.
  const [prefill, setPrefill] = useState(null);
  const smsHref = useEnquirySmsHref();
  useEffect(() => () => requestRef.current?.abort(), []);

  const showFieldErrors = (errors) => {
    setFieldErrors(errors);
    if (errors.email) setExtrasOpen(true);
    const first = Object.keys(errors)[0];
    requestAnimationFrame(() => formRef.current?.querySelector(`[name="${first}"]`)?.focus());
  };

  useEffect(() => {
    const onPrefill = (e) => {
      const d = e.detail || {};
      setPrefill(d);
      // The design lightbox hands over the exact sentence for the textarea
      // ("Hi Corey, I'd like a quote for: {design}"). Any text the visitor
      // already typed is kept underneath it.
      const msgLine = d.prefill_text
        ? `${d.prefill_text}\n\n`
        : d.category_label
          ? `I'd like to talk about ${d.category_label}.\n\n`
          : '';
      setForm((f) => ({ ...f, message: msgLine + (f.message || '') }));
      setEnquiryDraft({ design: d.design_name || d.category_label || '' });
    };
    window.addEventListener('ff-prefill-quote', onPrefill);
    return () => window.removeEventListener('ff-prefill-quote', onPrefill);
  }, []);

  // Publish the live draft for the SMS controls; clear it when this form
  // leaves the page so no other page inherits stale values.
  useEffect(() => {
    setEnquiryDraft({ service, suburb: form.suburb, timing: form.timing, message: form.message });
  }, [service, form.suburb, form.timing, form.message]);
  useEffect(() => () => resetEnquiryDraft(), []);

  const selected = SERVICE_BY_KEY[service];
  const composed = composeMessage({ serviceLabel: selected?.label, suburb: form.suburb, timing: form.timing, message: form.message });
  const remaining = MESSAGE_MAX - composed.length;

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (error) setError('');
    if (status === 'error') setStatus('idle');
    if (fieldErrors[e.target.name]) {
      setFieldErrors((fe) => ({ ...fe, [e.target.name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError('');
    const fe = {};
    if (form.name.trim().length < 2) fe.name = 'Please add your name (at least 2 characters).';
    else if (form.name.trim().length > 100) fe.name = 'Please keep your name under 101 characters.';
    if (!form.phone.trim()) fe.phone = 'Please add your phone number.';
    else if (!AU_PHONE_RE.test(normalisePhone(form.phone))) {
      fe.phone = 'That number doesn\u2019t look right \u2014 an Australian mobile or landline works best.';
    }
    if (form.message.trim().length < 3) fe.message = 'Please add a few words about the job (at least 3 characters).';
    else if (composed.length > MESSAGE_MAX) {
      fe.message = `That\u2019s ${composed.length - MESSAGE_MAX} characters over the ${MESSAGE_MAX}-character limit (the service, suburb and timing lines count towards it). Please shorten the job details, or text the rest to ${PHONE_DISPLAY}.`;
    }
    const emailTrimmed = form.email.trim();
    if (emailTrimmed && (emailTrimmed.length > 200 || !EMAIL_RE.test(emailTrimmed) || emailTrimmed.split('@')[0].includes('..'))) {
      fe.email = 'Please check your email address (up to 200 characters), or leave it blank.';
    }
    if (Object.keys(fe).length) {
      showFieldErrors(fe);
      return;
    }
    setFieldErrors({});
    submittingRef.current = true;
    setStatus('submitting');
    const controller = new AbortController();
    requestRef.current = controller;
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        // Service / suburb / timing travel ONCE, inside the existing message
        // field — the backend formatter renders it pre-wrap, no new fields.
        message: composed,
        // Optional — backend only sets Reply-To when a value is present.
        email: emailTrimmed || null,
        // Design pre-fill context — backend uses this to embed the image
        // link in the notification email so Corey sees the design directly.
        design_id: prefill?.design_id || null,
        design_name: prefill?.design_name || null,
        category: prefill?.category || null,
        image_url: prefill?.image_url || null,
      };
      const res = await fetch(`${API_BASE}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (res.status === 422) {
        const body = await res.json().catch(() => ({}));
        const errors = {};
        const messages = { name: 'Please check your name (2–100 characters).', phone: 'Please check your phone number.',
          email: 'Please check your email address, or leave it blank.', message: 'Please check the job details (3–1,500 characters, including optional details).' };
        for (const item of Array.isArray(body.detail) ? body.detail : []) {
          const field = item.loc?.[item.loc.length - 1];
          if (messages[field]) errors[field] = messages[field];
        }
        if (Object.keys(errors).length) showFieldErrors(errors);
        setStatus('error');
        setError('Please check the highlighted details. Everything you typed is still here.');
        return;
      }
      if (!res.ok) throw new Error('Request failed');
      trackEvent('quote_submit', { location: 'footer_form' });
      if (prefill?.design_id) {
        trackEvent('enquire_submit', { design_id: prefill.design_id, category: prefill.category });
      }
      setStatus('success');
      setForm(initial);
      setService(routeService);
      setPrefill(null);
    } catch (err) {
      // Entries stay exactly as typed; no success copy, no analytics event.
      // Pressing Send again issues exactly one new request.
      setStatus('error');
      setError(`We couldn’t confirm your enquiry was saved. Your details are still here. You can try again, or call or text ${PHONE_DISPLAY}.`);
    } finally {
      clearTimeout(timeout);
      submittingRef.current = false;
      requestRef.current = null;
    }
  };

  if (status === 'success') {
    return (
      <div className="quote-form-success" data-testid="quote-form-success" role="status">
        <CheckCircle2 size={40} strokeWidth={2} aria-hidden="true" />
        <p className="quote-success-title">Thanks &mdash; your enquiry has been saved.</p>
        <p className="quote-success-text">
          We&rsquo;ll review the details and contact you about the next step.
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} className="quote-form" onSubmit={handleSubmit} data-testid="quote-form" aria-busy={status === 'submitting'} noValidate>
      <p className="quote-form-intro">A few details. A proper conversation.</p>
      <p className="quote-form-note">Start with your name, number and what you have in mind.</p>

      {prefill?.design_name && (
        <div className="quote-form-prefill-chip" data-testid="quote-prefill-chip">
          Enquiring about: <strong>{prefill.design_name}</strong>
        </div>
      )}
      {prefill?.category_label && !prefill?.design_name && (
        <div className="quote-form-prefill-chip" data-testid="quote-prefill-chip">
          Category: <strong>{prefill.category_label}</strong>
        </div>
      )}

      <div className="quote-field">
        <label htmlFor="qf-name" className="quote-label">Your name</label>
        <input id="qf-name" name="name" type="text" className="quote-input" value={form.name}
          onChange={handleChange} autoComplete="name" placeholder="e.g. Margaret" maxLength={100}
          aria-invalid={fieldErrors.name ? 'true' : undefined}
          aria-describedby={fieldErrors.name ? 'qf-name-error' : undefined}
          data-testid="quote-name-input" required />
        {fieldErrors.name && (
          <p className="quote-field-error" id="qf-name-error" role="alert" data-testid="quote-name-error">{fieldErrors.name}</p>
        )}
      </div>
      <div className="quote-field">
        <label htmlFor="qf-phone" className="quote-label">Your phone number</label>
        <input id="qf-phone" name="phone" type="tel" className="quote-input" value={form.phone}
          onChange={handleChange} autoComplete="tel" placeholder="e.g. 04xx xxx xxx" maxLength={30} inputMode="tel"
          aria-invalid={fieldErrors.phone ? 'true' : undefined}
          aria-describedby={fieldErrors.phone ? 'qf-phone-error' : undefined}
          data-testid="quote-phone-input" required />
        {fieldErrors.phone && (
          <p className="quote-field-error" id="qf-phone-error" role="alert" data-testid="quote-phone-error">{fieldErrors.phone}</p>
        )}
      </div>
      <div className="quote-field">
        <label htmlFor="qf-message" className="quote-label">Tell me about the job</label>
        <textarea id="qf-message" name="message" className="quote-input quote-textarea"
          value={form.message} onChange={handleChange} rows={3}
          placeholder="e.g. a sticking door, a new fence, or one of the designs above"
          aria-invalid={fieldErrors.message ? 'true' : undefined}
          aria-describedby={[fieldErrors.message ? 'qf-message-error' : null, selected ? 'qf-guidance' : null].filter(Boolean).join(' ') || undefined}
          data-testid="quote-message-input" required />
        {selected && (
          <p className="quote-guidance" id="qf-guidance" data-testid="quote-guidance">{selected.guidance}</p>
        )}
        {remaining < 200 && (
          <p className={`quote-remaining${remaining < 0 ? ' quote-remaining--over' : ''}`} data-testid="quote-remaining" aria-live="polite">
            {remaining >= 0 ? `${remaining} characters left` : `${-remaining} characters over the limit`}
          </p>
        )}
        {fieldErrors.message && (
          <p className="quote-field-error" id="qf-message-error" role="alert" data-testid="quote-message-error">{fieldErrors.message}</p>
        )}
        {selected && (
          <p className="quote-photo-help" data-testid="quote-photo-help">
            Photos are optional. You can{' '}
            <a href={smsHref} className="quote-photo-link" data-testid="quote-photo-sms-link"
              onClick={() => trackEvent('sms_click', { location: 'form_photos' })}>
              {`text them to ${PHONE_DISPLAY}`}
            </a>
            {' '}with your name and suburb.
          </p>
        )}
      </div>
      <details className="quote-extras" open={extrasOpen} onToggle={(event) => setExtrasOpen(event.currentTarget.open)}>
        <summary>Add more detail <span>Email, suburb &amp; timing</span></summary>
        <div className="quote-extras-fields">
      <div className="quote-field">
        <label htmlFor="qf-email" className="quote-label">Email <span className="quote-label-hint">(optional)</span></label>
        <input id="qf-email" name="email" type="email" className="quote-input" value={form.email}
          onChange={handleChange} autoComplete="email" placeholder="so I can reply by email if easier"
          maxLength={200} aria-invalid={fieldErrors.email ? 'true' : undefined}
          aria-describedby={fieldErrors.email ? 'qf-email-error' : undefined}
          data-testid="quote-email-input" />
        {fieldErrors.email && <p className="quote-field-error" id="qf-email-error" role="alert">{fieldErrors.email}</p>}
      </div>

      <div className="quote-field">
        <label htmlFor="qf-service" className="quote-label">What do you need? <span className="quote-label-hint">(optional)</span></label>
        <select id="qf-service" name="service" className="quote-input quote-select" value={service}
          onChange={(e) => { setService(e.target.value); if (status === 'error') setStatus('idle'); }}
          data-testid="quote-service-select">
          <option value="">Not sure yet</option>
          {ENQUIRY_SERVICES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>
      <div className="quote-field-row">
        <div className="quote-field">
          <label htmlFor="qf-suburb" className="quote-label">Suburb <span className="quote-label-hint">(optional)</span></label>
          <input id="qf-suburb" name="suburb" type="text" className="quote-input" value={form.suburb}
            onChange={handleChange} autoComplete="address-level2" placeholder="e.g. Kingston" maxLength={80}
            data-testid="quote-suburb-input" />
        </div>
        <div className="quote-field">
          <label htmlFor="qf-timing" className="quote-label">Preferred timing <span className="quote-label-hint">(optional)</span></label>
          <input id="qf-timing" name="timing" type="text" className="quote-input" value={form.timing}
            onChange={handleChange} placeholder="e.g. weekday mornings, next month" maxLength={120}
            data-testid="quote-timing-input" />
        </div>
      </div>

        </div>
      </details>
      <p className="quote-privacy-note">Your details are used to respond to your enquiry.</p>
      {error && (
        <p className="quote-form-error" role="alert" data-testid="quote-form-error">{error}</p>
      )}
      <button type="submit" className="btn btn-primary quote-submit"
              disabled={status === 'submitting'} data-testid="quote-submit-btn">
        {status === 'submitting' ? (
          <><Loader2 size={22} strokeWidth={2.2} className="spin" aria-hidden="true" /><span>Sending&hellip;</span></>
        ) : (
          <><Send size={22} strokeWidth={2.2} aria-hidden="true" /><span>Send the details</span></>
        )}
      </button>
    </form>
  );
};

export default QuoteForm;
